/**
 * Admin V3 — Drivers module — top-level page
 *
 * Mounted at: /admin-v3/drivers (see AdminV3.js)
 *
 * SCOPE FOR PHASE 2.3:
 *   - Read-only: GET /admin/drivers (server-side status filter + paging)
 *   - Client-side search over the loaded page (name/email/phone/plate)
 *   - Per-row click → /admin-v3/drivers/:driverId (placeholder for now)
 *   - CSV export: full-dump from /admin/drivers/export/csv
 *
 * EXPLICIT ZERO MUTATIONS:
 *   - No POST/PATCH/PUT/DELETE; no activate/deactivate; no approve/reject;
 *     no idempotency keys; no confirmation modals
 *
 * Uses V3 foundation hook useAdminQuery (auth-memoized, abort-on-unmount).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Users, ChevronLeft, ChevronRight, UserPlus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import usePermissions from '../../shared/hooks/usePermissions';
import { t, DEFAULT_LOCALE } from '../../i18n';
import DriversListSearchBar from './DriversListSearchBar';
import DriversListTable from './DriversListTable';
import AddDriverModal from './AddDriverModal';

// P1-B3-3b: UI-only gate for the "Add driver" affordance. NOTE (AV3-31): the
// underlying POST /driver/register is a PUBLIC endpoint — this gate is FRONTEND
// ONLY and is NOT enforced server-side. A dedicated RBAC-protected
// POST /admin/drivers is a documented backend-hardening follow-up.
const ADD_DRIVER_UI_PERMISSION = 'admin:drivers:list';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 100;

const DriversListPage = () => {
  const locale = DEFAULT_LOCALE;

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);

  const { can } = usePermissions();
  const canAdd = can(ADD_DRIVER_UI_PERMISSION);

  // Debounce search input (300ms) — matches Bookings precedent
  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  // Reset page only when the server-side filter changes. Client-side search
  // is applied to the already-loaded page; resetting page on search change
  // would discard the data the user is searching within.
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  // Backend supports only status + page + limit. Search is client-side.
  const queryParams = { limit: PAGE_SIZE, page };
  if (statusFilter !== 'all') queryParams.status = statusFilter;

  const { data, loading, error, refetch } = useAdminQuery('/admin/drivers', queryParams);
  const driversRaw = Array.isArray(data) ? data : [];

  // Client-side search over the loaded page (case-insensitive)
  const drivers = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return driversRaw;
    return driversRaw.filter((d) =>
      (d.name || '').toLowerCase().includes(q) ||
      (d.email || '').toLowerCase().includes(q) ||
      (d.phone || '').toLowerCase().includes(q) ||
      (d.license_plate || '').toLowerCase().includes(q)
    );
  }, [driversRaw, debouncedSearch]);

  const isFilterActive = Boolean(debouncedSearch) || statusFilter !== 'all';
  // Pagination heuristic on the RAW server result (not the filtered view).
  // Accepted temporary limitation until backend exposes total_count.
  const canGoNext = driversRaw.length === PAGE_SIZE;
  const canGoPrev = page > 1;
  const pageLabel = t('drivers.page.current', locale).replace('{n}', String(page));

  return (
    <SectionCard
      title={t('drivers.title', locale)}
      icon={Users}
      iconClass="bg-emerald-100 text-emerald-600"
      action={canAdd ? (
        <Button
          variant="default"
          size="sm"
          onClick={() => setAddOpen(true)}
          className="rounded-xl"
          data-testid="adminv3-drivers-add-action"
        >
          <UserPlus className="h-3.5 w-3.5 mr-1.5" />
          {t('drivers.add.action', locale)}
        </Button>
      ) : null}
    >
      <AddDriverModal open={addOpen} onClose={() => setAddOpen(false)} onDone={refetch} locale={locale} />
      <div className="space-y-4">
        <DriversListSearchBar
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          resultsCount={drivers.length}
          isFilterActive={isFilterActive}
          loading={loading}
          locale={locale}
        />

        <DriversListTable
          drivers={drivers}
          loading={loading}
          error={error}
          isFilterActive={isFilterActive}
          onRetry={refetch}
          locale={locale}
        />

        {/* Pagination */}
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={!canGoPrev || loading}
            data-testid="adminv3-drivers-page-prev"
            className="rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <ChevronLeft className="h-3.5 w-3.5 mr-1" />
            {t('drivers.page.prev', locale)}
          </Button>
          <span className="text-xs text-slate-500 px-2" data-testid="adminv3-drivers-page-label">
            {pageLabel}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={!canGoNext || loading}
            data-testid="adminv3-drivers-page-next"
            className="rounded-xl border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            {t('drivers.page.next', locale)}
            <ChevronRight className="h-3.5 w-3.5 ml-1" />
          </Button>
        </div>
      </div>
    </SectionCard>
  );
};

export default DriversListPage;
